// "API keys" — Practice Details panel for the practice's machine API keys.
//
// Admin only (the backend refuses everyone else; this panel is not rendered for them).
// An admin mints a key for one or more scopes, sees the raw key ONCE, and can revoke any
// key. Several scopes on one key suit a sync that pushes and reads back (claims:import +
// ledger:read); the server stores them space-separated and checks each per request.
// Replaces running scripts/mint_api_key.py by hand; both write the same row.
//
// The raw key lives in component state only while its box is open. Closing the box,
// switching practice or unmounting drops it, and nothing re-fetches it: the server
// stores only a hash, so there is nothing to fetch. It is never logged or put in a URL.
// CSRF is attached by the wrapper index.js installs on window.fetch.
//
// A super-admin also sees "All practices (ledger snapshot only)": a key usable on every
// enabled practice, limited to ledger:snapshot. The server says whether to offer it
// (`can_create_all_practices`) and refuses anyone else regardless.

import React, { useEffect, useRef, useState } from "react";
import "./ApiKeysPanel.css";

const API_URL = process.env.REACT_APP_API_URL || "";

// What each scope lets a key do, in the words an admin needs to pick one. A scope the
// backend adds later still shows (by its name) rather than disappearing from the picker.
export const SCOPE_LABELS = {
  "claims:import": "Push claims into the ledger",
  "payments:import": "Push PracticeSuite patient payments against claims (posts money)",
  "bill:read": "External /bill pipeline: co-pays and patient rules",
  "ledger:read": "Read back what an import did (returns patient names)",
  "ledger:snapshot": "Ledger as data for outside reports (no patient identity)",
  "queue:work": "Cowork submit skill (returns patient demographics)",
  "auths:import": "Cowork auths import (push authorizations)",
  "auths:work": "Cowork auth-status skill (returns request packets)",
  "analytics:read": "Analyst MCP (read-only data access)",
};

const DEFAULT_SCOPE = "auths:work";
const DEFAULT_ALL_PRACTICES_SCOPE = "ledger:snapshot";
const LABEL_MAX = 100;

async function request(url, options, fallback) {
  const response = await fetch(url, { credentials: "include", ...options });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error((body && body.error) || fallback);
  }
  if (!body || typeof body !== "object") {
    throw new Error(fallback);
  }
  return body;
}

// Server times are naive UTC.
function formatTime(value) {
  if (!value) return "—";
  const text = String(value);
  const when = new Date(
    /[zZ]|[+-]\d{2}:?\d{2}$/.test(text.slice(10)) ? text : `${text}Z`,
  );
  return Number.isNaN(when.getTime()) ? "—" : when.toLocaleString();
}

export default function ApiKeysPanel({ practiceId, isAdmin }) {
  const [keys, setKeys] = useState(null);
  const [scopes, setScopes] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [message, setMessage] = useState(null);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [selected, setSelected] = useState([DEFAULT_SCOPE]);
  const [allPracticesScopes, setAllPracticesScopes] = useState([]);
  const [canAllPractices, setCanAllPractices] = useState(false);
  const [allPractices, setAllPractices] = useState(false);
  const [formError, setFormError] = useState(null);
  const [revealed, setRevealed] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const currentPractice = useRef(practiceId);
  currentPractice.current = practiceId;

  useEffect(() => {
    // A key revealed for one practice must never be on screen for the next.
    setKeys(null);
    setLoadError(null);
    setMessage(null);
    setCreating(false);
    setRevealed(null);
    setCopied(false);
    setRevokeTarget(null);
    if (!practiceId || !isAdmin) return undefined;
    let cancelled = false;
    request(
      `${API_URL}/api/practices/${practiceId}/api-keys`,
      {},
      "Could not load API keys.",
    )
      .then((body) => {
        if (cancelled) return;
        setKeys(Array.isArray(body.api_keys) ? body.api_keys : []);
        setScopes(Array.isArray(body.scopes) ? body.scopes : []);
        setAllPracticesScopes(
          Array.isArray(body.all_practices_scopes)
            ? body.all_practices_scopes
            : [],
        );
        setCanAllPractices(body.can_create_all_practices === true);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, isAdmin]);

  if (!practiceId || !isAdmin) return null;

  const openCreate = () => {
    setLabel("");
    setSelected([
      scopes.includes(DEFAULT_SCOPE) || !scopes.length
        ? DEFAULT_SCOPE
        : scopes[0],
    ]);
    setAllPractices(false);
    setFormError(null);
    setMessage(null);
    setCreating(true);
  };

  // An all-practices key may only carry ledger:snapshot, so the picker narrows to it.
  const toggleAllPractices = (checked) => {
    setAllPractices(checked);
    if (!checked) return;
    const kept = selected.filter((s) => allPracticesScopes.includes(s));
    if (kept.length) {
      setSelected(kept);
    } else if (allPracticesScopes.includes(DEFAULT_ALL_PRACTICES_SCOPE)) {
      setSelected([DEFAULT_ALL_PRACTICES_SCOPE]);
    } else {
      setSelected(allPracticesScopes.slice(0, 1));
    }
  };
  const toggleScope = (s, checked) => {
    setSelected((prev) =>
      checked ? [...prev, s] : prev.filter((x) => x !== s),
    );
  };
  const pickable = allPractices
    ? allPracticesScopes
    : scopes.length
      ? scopes
      : [DEFAULT_SCOPE];
  // What goes up: the ticked scopes, in the picker's order, never one it no longer offers.
  const chosen = pickable.filter((s) => selected.includes(s));

  const closeReveal = () => {
    setRevealed(null);
    setCopied(false);
  };

  const create = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setFormError(null);
    const forPractice = practiceId;
    try {
      const body = await request(
        `${API_URL}/api/practices/${forPractice}/api-keys`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            label: label.trim() || null,
            scopes: chosen,
            all_practices: allPractices,
          }),
        },
        "Could not create the API key.",
      );
      if (currentPractice.current !== forPractice) return;
      setKeys((prev) => [body.api_key, ...(prev || [])]);
      setCreating(false);
      setCopied(false);
      setRevealed({
        key: body.key,
        prefix: body.api_key.key_prefix,
        scopes: body.api_key.scopes,
        allPractices: body.api_key.all_practices === true,
      });
    } catch (err) {
      if (currentPractice.current !== forPractice) return;
      setFormError(err.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (busyRef.current || !revokeTarget) return;
    busyRef.current = true;
    setBusy(true);
    const forPractice = practiceId;
    const target = revokeTarget;
    try {
      const body = await request(
        `${API_URL}/api/practices/${forPractice}/api-keys/${target.id}/revoke`,
        { method: "POST" },
        "Could not revoke the API key.",
      );
      if (currentPractice.current !== forPractice) return;
      setKeys((prev) =>
        (prev || []).map((k) => (k.id === body.api_key.id ? body.api_key : k)),
      );
      setMessage({
        type: "success",
        text: `Revoked ${target.key_prefix}. It stops working now.`,
      });
    } catch (err) {
      if (currentPractice.current !== forPractice) return;
      setMessage({ type: "error", text: err.message });
    } finally {
      busyRef.current = false;
      setBusy(false);
      setRevokeTarget(null);
    }
  };

  const copy = async () => {
    if (!revealed) return;
    try {
      // navigator.clipboard needs a secure context; staging and production are HTTPS.
      await navigator.clipboard.writeText(revealed.key);
      setCopied(true);
    } catch (err) {
      setCopied(false);
      setMessage({
        type: "error",
        text: "Could not copy. Select the key and copy it by hand.",
      });
    }
  };

  return (
    <div className="integrations-section api-keys-panel">
      <div className="api-keys-head">
        <h3>API keys</h3>
        {keys && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={openCreate}
            disabled={busy}
          >
            Create key
          </button>
        )}
      </div>
      <p className="api-keys-intro">
        Machine keys for this practice's skills and imports. Each key grants
        the scopes ticked when it is made, for this practice only unless it is
        marked All practices. A key is shown once, when it is created.
      </p>

      {message && (
        <div className={`field-message ${message.type} api-keys-message`}>
          {message.text}
          <button
            type="button"
            className="message-close-sm"
            onClick={() => setMessage(null)}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}

      {revealed && (
        <div className="api-keys-reveal" role="alert">
          <div className="api-keys-reveal-head">
            <strong>
              New {revealed.scopes} key
              {revealed.allPractices ? " for all practices" : ""} — copy it now.
              You will not see this again.
            </strong>
            <button
              type="button"
              className="message-close-sm"
              onClick={closeReveal}
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
          <div className="api-keys-reveal-row">
            <code className="api-keys-raw" data-testid="api-key-raw">
              {revealed.key}
            </code>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={copy}
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="api-keys-reveal-note">
            Put it in <code>ATLAS_API_KEY</code> on the machine that runs the
            skill — never in chat, a command line, or a page. Only its hash is
            stored; if it is lost, revoke it and create another.
          </p>
          <div className="api-keys-reveal-actions">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={closeReveal}
            >
              I have copied it
            </button>
          </div>
        </div>
      )}

      {loadError ? (
        <div className="field-message error">{loadError}</div>
      ) : keys === null ? (
        <p className="api-keys-muted">Loading…</p>
      ) : keys.length === 0 ? (
        <p className="api-keys-muted">No keys for this practice.</p>
      ) : (
        <table className="detail-table api-keys-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Label</th>
              <th>Scope</th>
              <th>Created</th>
              <th>Last used</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} className={k.revoked ? "api-keys-revoked" : ""}>
                <td>
                  <code>{k.key_prefix}…</code>
                </td>
                <td>{k.label || <span className="api-keys-muted">—</span>}</td>
                <td>
                  <code>{k.scopes}</code>
                  {k.all_practices && (
                    <span
                      className="status-badge api-keys-all"
                      title="Reads every enabled practice, not only this one"
                    >
                      All practices
                    </span>
                  )}
                </td>
                <td>{formatTime(k.created_at)}</td>
                <td>
                  {k.last_used_at ? (
                    formatTime(k.last_used_at)
                  ) : (
                    <span className="api-keys-muted">never</span>
                  )}
                </td>
                <td>
                  {k.revoked ? (
                    <span
                      className="status-badge inactive"
                      title={`Revoked ${formatTime(k.revoked_at)}`}
                    >
                      Revoked
                    </span>
                  ) : (
                    <>
                      <span className="status-badge active">Active</span>
                      <button
                        type="button"
                        className="btn-link danger"
                        onClick={() => setRevokeTarget(k)}
                        disabled={busy}
                      >
                        Revoke
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {creating && (
        <div
          className="modal-overlay"
          onClick={busy ? undefined : () => setCreating(false)}
        >
          <div
            className="create-modal"
            role="dialog"
            aria-label="Create API key"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Create API key</h2>
            {formError && <div className="form-message error">{formError}</div>}
            <div className="form-field api-keys-field">
              <label htmlFor="api-key-label">Label</label>
              <input
                id="api-key-label"
                value={label}
                maxLength={LABEL_MAX}
                placeholder="e.g. cowork auth-status — David's laptop"
                onChange={(e) => setLabel(e.target.value)}
                disabled={busy}
              />
            </div>
            <fieldset className="form-field api-keys-field api-keys-scopes">
              <legend>Scopes</legend>
              {pickable.map((s) => (
                <label key={s} className="api-keys-scope">
                  <input
                    type="checkbox"
                    id={`api-key-scope-${s.replace(":", "-")}`}
                    value={s}
                    checked={chosen.includes(s)}
                    onChange={(e) => toggleScope(s, e.target.checked)}
                    disabled={busy}
                  />
                  <span>
                    <code>{s}</code>
                    {SCOPE_LABELS[s] ? ` — ${SCOPE_LABELS[s]}` : ""}
                  </span>
                </label>
              ))}
              <p className="api-keys-muted api-keys-scopes-hint">
                One key can carry several scopes: a sync that pushes claims and
                reads back what landed takes <code>claims:import</code> and{" "}
                <code>ledger:read</code> together.
              </p>
            </fieldset>
            {canAllPractices && (
              <div className="form-field api-keys-field api-keys-check">
                <label htmlFor="api-key-all-practices">
                  <input
                    id="api-key-all-practices"
                    type="checkbox"
                    checked={allPractices}
                    onChange={(e) => toggleAllPractices(e.target.checked)}
                    disabled={busy}
                  />{" "}
                  All practices (ledger snapshot only)
                </label>
              </div>
            )}
            <p className="api-keys-muted">
              {!chosen.length ? (
                <>Tick at least one scope.</>
              ) : allPractices ? (
                <>
                  This creates a <code>{chosen.join(" ")}</code> key for{" "}
                  <strong>every enabled practice</strong>, not only this one.
                  It can only read the ledger snapshot, which has no patient
                  identity.
                </>
              ) : (
                <>
                  This creates a <code>{chosen.join(" ")}</code> key for this
                  practice.
                </>
              )}{" "}
              Anyone holding it can use {chosen.length > 1 ? "those scopes" : "that scope"}{" "}
              until it is revoked. The key is shown once, on the next screen.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setCreating(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={create}
                disabled={busy || !chosen.length}
              >
                {busy ? "Creating…" : "Create key"}
              </button>
            </div>
          </div>
        </div>
      )}

      {revokeTarget && (
        <div
          className="modal-overlay"
          onClick={busy ? undefined : () => setRevokeTarget(null)}
        >
          <div
            className="create-modal"
            role="dialog"
            aria-label="Revoke API key"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Revoke API key?</h2>
            <p className="api-keys-confirm-text">
              <code>{revokeTarget.key_prefix}…</code>
              {revokeTarget.label ? ` (${revokeTarget.label})` : ""} —{" "}
              <code>{revokeTarget.scopes}</code>. Anything using this key stops
              working immediately. This cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setRevokeTarget(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary api-keys-danger"
                onClick={revoke}
                disabled={busy}
              >
                {busy ? "Revoking…" : "Revoke key"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
